/**
 * A VideoMail recording session carried across the page load into its room.
 *
 * On the web, entering another room loads a new page
 * (app/getRouteToRender.web.ts: a room change sets window.location.href),
 * and that drops the Redux recording session: the recording room opened
 * with nothing to record it. The client portal (client-profile.js) always
 * enters rooms by loading the page. So the session is written here before
 * the navigation, by the room app and by the portal, and read back when the
 * page opens that room.
 *
 * It is used only for its own room (voicemail-<id> or
 * videomail-greeting-<id>), only by the account that wrote it, and only for
 * 30 minutes. The server has already checked it: the room token for that
 * room is signed only for the recording's owner while the recording is open
 * (vrs-server/src/db/room-access.ts), and the upload route checks the owner
 * again.
 */
import { getPersistentJson, removePersistentItem, setPersistentItem } from '../vrs-auth/storage';
/** Also written by client-profile.js (leaveVideoMail); keep the shape in step. */
export const VIDEOMAIL_SESSION_KEY = 'vrs_videomail_session';
const MAX_AGE_MS = 30 * 60 * 1000;
const RECORDING_ROOM = /^(voicemail|videomail-greeting)-([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;
const MEDIA_URL = /^https?:\/\//i;
/**
 * The callee's greeting that the recording page plays first, when the record
 * carries one with media links (VoicemailRecording.tsx).
 */
function greetingOf(value) {
    const greeting = value;
    if (typeof greeting?.playbackUrl !== 'string' || !MEDIA_URL.test(greeting.playbackUrl)) {
        return {};
    }
    return { calleeGreeting: {
            playbackUrl: greeting.playbackUrl,
            ...(typeof greeting.thumbnailUrl === 'string' && MEDIA_URL.test(greeting.thumbnailUrl)
                ? { thumbnailUrl: greeting.thumbnailUrl } : {})
        } };
}
function signedInUserId() {
    const id = getPersistentJson('vrs_user_info')?.id;
    return typeof id === 'string' && id ? id : undefined;
}
/** Whether a room name is a VideoMail recording room. */
export function isRecordingRoomName(roomName) {
    return typeof roomName === 'string' && RECORDING_ROOM.test(roomName);
}
/** Keep the session this device is about to record, for the page that opens its room. */
export function rememberRecordingSession(session) {
    const userId = signedInUserId();
    if (!userId || !session?.messageId || !isRecordingRoomName(session.roomName))
        return;
    setPersistentItem(VIDEOMAIL_SESSION_KEY, JSON.stringify({
        owner: { userId },
        session,
        v: 1,
        writtenAt: Date.now()
    }));
}
/**
 * The session for the room this page opened, or null: another room, another
 * account, a stale or damaged record.
 */
export function restoreRecordingSession(roomName) {
    const stored = getPersistentJson(VIDEOMAIL_SESSION_KEY);
    const session = stored?.session;
    const match = typeof roomName === 'string' ? RECORDING_ROOM.exec(roomName) : null;
    if (!stored || stored.v !== 1 || !session || !match)
        return null;
    if (typeof stored.writtenAt !== 'number' || Date.now() - stored.writtenAt > MAX_AGE_MS) {
        forgetRecordingSession();
        return null;
    }
    if (!stored.owner?.userId || stored.owner.userId !== signedInUserId())
        return null;
    if (session.roomName !== roomName || session.messageId?.toLowerCase() !== match[2].toLowerCase())
        return null;
    if (typeof session.maxDurationSeconds !== 'number' || !(session.maxDurationSeconds > 0))
        return null;
    return {
        kind: match[1].toLowerCase() === 'voicemail' ? 'message' : 'greeting',
        maxDurationSeconds: session.maxDurationSeconds,
        messageId: session.messageId,
        roomName: session.roomName,
        ...(typeof session.calleeId === 'string' ? { calleeId: session.calleeId } : {}),
        ...(typeof session.calleeName === 'string' ? { calleeName: session.calleeName } : {}),
        ...(typeof session.calleePhone === 'string' ? { calleePhone: session.calleePhone } : {}),
        ...(match[1].toLowerCase() === 'voicemail' ? greetingOf(session.calleeGreeting) : {})
    };
}
/**
 * The recording was sent or cancelled: nothing to carry any more. With a
 * message id, only that recording's record goes: "Record again" cancels the
 * old take after the new one was remembered.
 */
export function forgetRecordingSession(messageId) {
    if (messageId) {
        const stored = getPersistentJson(VIDEOMAIL_SESSION_KEY);
        if (stored?.session?.messageId && stored.session.messageId !== messageId)
            return;
    }
    removePersistentItem(VIDEOMAIL_SESSION_KEY);
}
